import type { TripDay, TripEvent } from './types';

const DB_NAME = 'europa-2026-planning';
const DB_VERSION = 1;
const STORE = 'state';
const KEY = 'planning-v1';

export interface PlanningOverride {
  date?: string;
  time?: string;
  title?: string;
  subtitle?: string;
  status?: TripEvent['status'];
}

export interface PlanningAddition {
  date: string;
  event: TripEvent;
}

export interface PlanningState {
  overrides: Record<string, PlanningOverride>;
  additions: PlanningAddition[];
  updatedAt?: string;
}

export const emptyPlanningState: PlanningState = { overrides: {}, additions: [] };

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function loadPlanningState(): Promise<PlanningState> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const request = tx.objectStore(STORE).get(KEY);
    request.onsuccess = () => resolve((request.result as PlanningState | undefined) ?? emptyPlanningState);
    request.onerror = () => reject(request.error);
  });
}

export async function savePlanningState(state: PlanningState): Promise<void> {
  const db = await openDb();
  const next = { ...state, updatedAt: new Date().toISOString() };
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(next, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function cloneEvent(event: TripEvent): TripEvent {
  return {
    ...event,
    details: event.details ? [...event.details] : undefined,
    passengers: event.passengers ? event.passengers.map((passenger) => ({ ...passenger })) : undefined,
    route: event.route ? { ...event.route } : undefined,
  };
}

export function applyPlanningState(base: TripDay[], state: PlanningState): TripDay[] {
  const dayMap = new Map<string, TripDay>();
  for (const day of base) dayMap.set(day.date, { ...day, events: [] });

  for (const day of base) {
    for (const sourceEvent of day.events) {
      const override = state.overrides[sourceEvent.id] ?? {};
      const targetDate = override.date ?? day.date;
      const event: TripEvent = {
        ...cloneEvent(sourceEvent),
        ...(override.time !== undefined ? { time: override.time || undefined } : {}),
        ...(override.title !== undefined ? { title: override.title } : {}),
        ...(override.subtitle !== undefined ? { subtitle: override.subtitle || undefined } : {}),
        ...(override.status !== undefined ? { status: override.status } : {}),
      };
      let target = dayMap.get(targetDate);
      if (!target) {
        target = { date: targetDate, city: day.city, country: day.country, events: [] };
        dayMap.set(targetDate, target);
      }
      target.events.push(event);
    }
  }

  for (const addition of state.additions) {
    let target = dayMap.get(addition.date);
    if (!target) {
      target = { date: addition.date, city: 'Actividad añadida', country: 'Europa', events: [] };
      dayMap.set(addition.date, target);
    }
    target.events.push(cloneEvent(addition.event));
  }

  return [...dayMap.values()]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((day) => ({
      ...day,
      events: [...day.events].sort((a, b) => (a.time ?? '99:99').localeCompare(b.time ?? '99:99')),
    }));
}
